import React, { useRef } from 'react';

interface RadioGroupOption<T extends string> {
  value: T;
  /** Usually a plain string. Widened to a node for options whose label is more than a phrase — a
   * transformation on a second line, say — since the accessible name still comes from whatever is
   * rendered inside the radio. */
  label: React.ReactNode;
  /** Unavailable given the block's current state — greyed out and inert but still listed, never
   * removed from view (matches CheckboxRow's own disabled convention: a teacher should be able to
   * see every option that exists, not just the ones currently selectable). */
  disabled?: boolean;
  /** Id of an element (typically a hint/warning paragraph) explaining why this option is
   * disabled — only meaningful alongside `disabled`. */
  describedBy?: string;
  /** Sub-options belonging to this option specifically, rendered indented directly beneath it
   * rather than after the whole list. Needed whenever the parent option isn't last: Compass's
   * display-format choice belongs to "Show Live Reading", which is the FIRST of three modes, so
   * appending it below the group would visually nest it under the last one instead. (Ordering's
   * circular-direction group gets away with sitting after its own group only because Circular
   * happens to be the final shape.) Rendered outside the radio buttons themselves, so it never
   * lands inside a `role="radio"` element. */
  nestedContent?: React.ReactNode;
}

interface RadioGroupProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: RadioGroupOption<T>[];
  'aria-label'?: string;
  /** Names the data key this control writes, so an undo can pulse the whole control rather than
   * the whole block. A mode switch is one choice, so the tag belongs on the group, not a segment. */
  fieldId?: string;
}

export function RadioGroup<T extends string>({ value, onChange, options, 'aria-label': ariaLabel, fieldId }: RadioGroupProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null);

  function handleKeyDown(e: React.KeyboardEvent, idx: number) {
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % options.length;
    else if (e.key === 'ArrowUp'   || e.key === 'ArrowLeft')  next = (idx - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End')  next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    if (options[next]!.disabled) return;
    onChange(options[next]!.value);
    const btns = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    btns?.[next]?.focus();
  }

  const selectedIdx = options.findIndex(o => o.value === value);
  const focusIdx = selectedIdx >= 0 ? selectedIdx : 0;

  return (
    <div ref={groupRef} role='radiogroup' aria-label={ariaLabel} data-field-id={fieldId} className='flex flex-col gap-2'>
      {options.map((opt, idx) => {
        const isSelected = opt.value === value;
        const radio = (
          <button
            key={opt.value}
            type='button'
            role='radio'
            aria-checked={isSelected}
            aria-disabled={opt.disabled || undefined}
            aria-describedby={opt.describedBy}
            tabIndex={idx === focusIdx ? 0 : -1}
            onClick={opt.disabled ? undefined : () => onChange(opt.value)}
            onKeyDown={e => handleKeyDown(e, idx)}
            className={`flex items-center gap-2.5 text-left ${opt.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
          >
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
              isSelected ? 'border-ws-primary' : 'border-ws-border bg-ws-btn'
            }`}>
              {isSelected && <span className='h-2 w-2 rounded-full bg-ws-primary' />}
            </span>
            <span className='text-sm text-ws-text'>{opt.label}</span>
          </button>
        );
        if (!opt.nestedContent) return radio;
        return (
          <div key={opt.value} className='flex flex-col gap-2'>
            {radio}
            <div className='ml-7'>{opt.nestedContent}</div>
          </div>
        );
      })}
    </div>
  );
}
